"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n";
import ThemeToggler from "./ThemeToggler";
import LanguageSelector from "./LanguageSelector";
import menuData from "./menuData";
import { ArrowRight, Menu, X } from "../Common/Icons";

const Header = () => {
  const tNav = useTranslations("component.navigation");
  const tSection = useTranslations("component.navigation.section");

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu when the viewport grows past the breakpoint.
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (window.innerWidth >= 992) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300 ${
        solid
          ? "border-stroke/80 bg-white/95 dark:border-stroke-dark/80 dark:bg-black/90"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="container">
        <div
          className={`flex items-center justify-between transition-[height] duration-300 ${
            solid ? "h-16" : "h-20"
          }`}
        >
          <Link
            href="/"
            className="relative block shrink-0"
            aria-label="Devest Solutions"
          >
            <Image
              unoptimized
              src="/images/logo/logo-2.webp"
              alt="Devest Solutions"
              width={124}
              height={40}
              priority
              className="h-8 w-auto dark:hidden"
            />
            <Image
              unoptimized
              src="/images/logo/logo.webp"
              alt="Devest Solutions"
              width={124}
              height={40}
              priority
              className="hidden h-8 w-auto dark:block"
            />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {menuData.map((item) => (
              <Link
                key={item.id}
                href={item.path}
                className="rounded-full px-4 py-2 text-sm font-medium text-body-color transition hover:bg-gray-light hover:text-black dark:text-body-color-dark dark:hover:bg-white/10 dark:hover:text-white"
              >
                {tSection(`${item.code}.label`)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggler />
            <LanguageSelector />
            <Link
              href="/#contact"
              className="ml-2 btn-primary hidden py-2.5! lg:inline-flex"
            >
              {tSection("contact.label")}
              <ArrowRight size={16} />
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? tNav("closeButton") : tNav("menuButton")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-black transition hover:bg-gray-light lg:hidden dark:text-white dark:hover:bg-white/10"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      <div
        id="mobile-nav"
        className={`overflow-hidden border-t border-stroke transition-all duration-300 lg:hidden dark:border-stroke-dark ${
          open ? "max-h-96 opacity-100" : "max-h-0 border-t-0 opacity-0"
        }`}
      >
        <nav className="container flex flex-col gap-1 py-4" aria-label="Mobile">
          {menuData.map((item) => (
            <Link
              key={item.id}
              href={item.path}
              onClick={() => setOpen(false)}
              className="rounded-xl px-4 py-3 text-base font-medium text-black transition hover:bg-gray-light dark:text-white dark:hover:bg-white/10"
            >
              {tSection(`${item.code}.label`)}
            </Link>
          ))}
          <Link
            href="/#contact"
            onClick={() => setOpen(false)}
            className="mt-2 btn-primary"
          >
            {tSection("contact.label")}
            <ArrowRight size={16} />
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
